import React from 'react';
import { usePrevious } from 'react-use';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

type ScrollIntoViewProps = React.HTMLAttributes<HTMLDivElement> &
  React.PropsWithChildren<{
    enabled?: boolean;
    asChild?: boolean;
    focus?: boolean;
    forceScroll?: boolean;
  }>;

export const ScrollIntoView = ({
  enabled,
  asChild,
  children,
  focus = false,
  forceScroll = false,
  ...props
}: ScrollIntoViewProps) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const lastEnabled = usePrevious(enabled);

  React.useLayoutEffect(() => {
    if (!lastEnabled && enabled && ref?.current) {
      if (forceScroll) {
        ref.current.scrollIntoView({ block: focus ? 'start' : 'nearest' });
      } else {
        scrollIntoViewIfNeeded(ref.current, focus);
      }
    }
  }, [enabled, lastEnabled, ref, focus, forceScroll]);

  if (asChild) {
    if (React.Children.count(children) !== 1) {
      throw new Error('The `asChild` prop only works when `children` is a single React element.');
    }

    const child = React.Children.only(children) as React.ReactElement;
    return React.cloneElement(child, { ...props, ref });
  }

  return (
    <div {...props} ref={ref}>
      {children}
    </div>
  );
};
